// Phase 5 (web revival) — virtualized photo grid.
//
// Owns the react-window grid + day dividers + lazy-paging trigger.
// Used by /library, /shared-with-me, and /album/$id. Each consumer
// hands in:
//   - a flat array of AssetDTOs (already filtered + concatenated across
//     loaded pages by the caller's useInfiniteQuery)
//   - hasNextPage / fetchNextPage from the same useInfiniteQuery
//   - a `scrollKey` naming this list for scroll restoration
//
// Layout strategy: day-grouped rows, Google-Photos style. Each calendar
// day gets one header row (full-width, labelled by weekday/month/day/year,
// with Today/Yesterday shortcuts) followed by N photo rows of
// `columnCount` cells. A floating month/year pill (top-left) tracks the
// topmost visible section as you scroll, so you always know "where" you
// are in the timeline. Using VariableSizeGrid keeps the math simple
// (header rows are short, photo rows are square) and lets us trigger
// fetchNextPage from the grid's `onItemsRendered` once the user is within
// a few rows of the bottom.
//
// All date bucketing is done in UTC (matching `capture_time_utc`), and the
// labels are formatted with `timeZone: 'UTC'` so a photo never lands under
// a header dated a day off from its own label near midnight.
//
// Scroll restoration: react-window scrolls an inner div, so the router
// can't restore it. We save scrollTop per `scrollKey` into module-level
// memory (app/scrollMemory.ts) on every scroll and replay it via
// `initialScrollTop` on remount — works because TanStack Query serves
// the previously-loaded pages synchronously from cache, so the grid has
// its full height back on first paint. If the lightbox recorded a "last
// viewed asset" (user arrowed away from their entry point), we
// additionally scroll that photo into view once the data lands.
//
// 10k+ photos: only ~30 rows ever materialize at once thanks to
// react-window's windowing; the rest are scroll spacers.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { VariableSizeGrid } from 'react-window';
import type {
  GridChildComponentProps,
  GridOnItemsRenderedProps,
  GridOnScrollProps,
} from 'react-window';
import { Link } from '@tanstack/react-router';
import type { AssetDTO } from '../../api/types';
import {
  consumeViewedAsset,
  getScrollOffset,
  saveScrollOffset,
} from '../../app/scrollMemory';
import { Thumbnail } from './Thumbnail';

interface TimelineGridProps {
  assets: AssetDTO[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  /// Optional copy shown when `assets.length === 0` and we're not
  /// fetching. Defaults to "No photos."
  emptyLabel?: string;
  /// Selection mode. When true, tiles show a checkbox, a click toggles
  /// selection instead of navigating to the detail page, and selected tiles
  /// get a ring + inset. The owner state lives in the parent.
  selectable?: boolean;
  selectedIds?: ReadonlySet<string>;
  onToggleSelect?: (id: string) => void;
  /// Stable name for this list's scroll position ("library:all",
  /// "album:<id>", …). Lists with different content must use different
  /// keys or they'll inherit each other's offsets.
  scrollKey?: string;
}

type RowDescriptor =
  | { kind: 'header'; key: string; label: string; monthYear: string }
  | { kind: 'photos'; key: string; firstAssetIndex: number; count: number };

const HEADER_ROW_HEIGHT = 44;
const PHOTO_ROW_GAP = 4;
const TARGET_COLUMN_WIDTH = 200; // ~5-6 columns on a typical desktop.
const MIN_COLUMNS = 3;
const MAX_COLUMNS = 8;
const FETCH_AHEAD_ROWS = 5;
const MS_PER_DAY = 86_400_000;

function monthLabel(d: Date): string {
  return d.toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function dayKey(d: Date): string {
  // YYYY-MM-DD (UTC) — stable, sort-friendly, cheap.
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(
    d.getUTCDate(),
  ).padStart(2, '0')}`;
}

// Whole-day difference between two instants, measured in UTC calendar days
// (so 23:59 → 00:01 the next day counts as 1, not 0).
function utcDayDelta(from: Date, to: Date): number {
  const a = Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate());
  const b = Date.UTC(to.getUTCFullYear(), to.getUTCMonth(), to.getUTCDate());
  return Math.round((b - a) / MS_PER_DAY);
}

function dayLabel(d: Date, now: Date): string {
  const delta = utcDayDelta(d, now);
  if (delta === 0) return 'Today';
  if (delta === 1) return 'Yesterday';
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function assetTime(a: AssetDTO): Date {
  // Fall back to upload time so assets without a capture time still group.
  const src = a.capture_time_utc ?? a.created_at;
  const d = new Date(src);
  if (Number.isNaN(d.getTime())) return new Date(0);
  // The grid formats every date with `timeZone: 'UTC'`. When we know the
  // capture's timezone offset, shift the instant by it so the UTC-formatted
  // label shows the capture-location WALL CLOCK ("the time it was taken"),
  // not raw UTC and not the viewer's local time. Without an offset (older
  // photos with no OffsetTimeOriginal, which were stored as wall-clock-as-UTC)
  // the unshifted UTC render already shows the right wall clock.
  if (a.capture_time_utc != null && a.capture_tz_offset_seconds != null) {
    return new Date(d.getTime() + a.capture_tz_offset_seconds * 1000);
  }
  return d;
}

interface GroupedAssets {
  rows: RowDescriptor[];
}

function groupByDay(assets: AssetDTO[], columnCount: number, now: Date): GroupedAssets {
  if (assets.length === 0) return { rows: [] };
  // We trust the server's default ordering (capture_time_utc DESC). If
  // it ever drifts, sort here defensively — but for now the inbound
  // order IS the timeline order.
  const rows: RowDescriptor[] = [];
  let currentDay: string | null = null;
  let i = 0;
  while (i < assets.length) {
    const t = assetTime(assets[i]!);
    const dk = dayKey(t);
    if (dk !== currentDay) {
      currentDay = dk;
      rows.push({
        kind: 'header',
        key: `h:${dk}`,
        label: dayLabel(t, now),
        monthYear: monthLabel(t),
      });
    }
    // Greedily pack all consecutive assets in the same day into
    // photo-rows of `columnCount`.
    const dayStart = i;
    let j = i;
    while (j < assets.length && dayKey(assetTime(assets[j]!)) === dk) j++;
    const dayLen = j - dayStart;
    let placed = 0;
    while (placed < dayLen) {
      const take = Math.min(columnCount, dayLen - placed);
      rows.push({
        kind: 'photos',
        key: `p:${dk}:${dayStart + placed}`,
        firstAssetIndex: dayStart + placed,
        count: take,
      });
      placed += take;
    }
    i = j;
  }
  return { rows };
}

function useContainerSize(): {
  ref: React.RefObject<HTMLDivElement | null>;
  width: number;
  height: number;
} {
  const ref = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setSize({ width: Math.floor(width), height: Math.floor(height) });
    });
    ro.observe(el);
    // Seed with the current size synchronously so the first render has
    // dimensions and react-window doesn't paint an empty 0×0 grid.
    const rect = el.getBoundingClientRect();
    setSize({ width: Math.floor(rect.width), height: Math.floor(rect.height) });
    return () => ro.disconnect();
  }, []);
  return { ref, width: size.width, height: size.height };
}

interface CellData {
  rows: RowDescriptor[];
  assets: AssetDTO[];
  columnCount: number;
  cellSize: number;
  selectable: boolean;
  selectedIds?: ReadonlySet<string>;
  onToggleSelect?: (id: string) => void;
}

function Cell({ columnIndex, rowIndex, style, data }: GridChildComponentProps<CellData>) {
  const row = data.rows[rowIndex];
  if (!row) return null;
  if (row.kind === 'header') {
    if (columnIndex !== 0) return null;
    return (
      <div
        style={{ ...style, width: data.columnCount * data.cellSize }}
        className="flex items-end px-2.5 pb-1.5 font-display text-[15px] font-medium tracking-tight text-neutral-200"
      >
        {row.label}
      </div>
    );
  }
  if (columnIndex >= row.count) {
    // Empty pad cells at the tail of a partial day-row.
    return <div style={style} />;
  }
  const asset = data.assets[row.firstAssetIndex + columnIndex];
  if (!asset) return <div style={style} />;
  const selected = data.selectedIds?.has(asset.id) ?? false;
  return (
    <div style={style} className="p-[2px]">
      <div className="group relative h-full w-full">
        <Link
          to="/asset/$assetID"
          params={{ assetID: asset.id }}
          onClick={(e) => {
            // In select mode a tap toggles selection rather than navigating.
            if (data.selectable) {
              e.preventDefault();
              data.onToggleSelect?.(asset.id);
            }
          }}
          className={
            'relative block h-full w-full overflow-hidden rounded-[3px] bg-neutral-900 transition-all ' +
            (selected ? 'scale-[0.88] ring-2 ring-sky-500' : '')
          }
        >
          <Thumbnail
            assetID={asset.id}
            size={256}
            className="h-full w-full object-cover transition duration-300 ease-out group-hover:scale-[1.04] group-hover:brightness-110"
          />
          {/* Hairline inner ring on hover — reads as a lift without any
              layout shift or bleed into the neighbors. */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-[3px] ring-1 ring-inset ring-white/25 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
          />
          {asset.kind === 'video' && (
            <span
              aria-label="Video"
              className="absolute bottom-1.5 left-1.5 flex size-5 items-center justify-center rounded-full bg-black/55 backdrop-blur-sm"
            >
              <svg width="9" height="9" viewBox="0 0 10 10" aria-hidden>
                <path d="M2 1.2 8.6 5 2 8.8Z" fill="white" />
              </svg>
            </span>
          )}
          {asset.favorite && (
            <svg
              aria-label="Favorite"
              className="absolute right-1.5 top-1.5 drop-shadow-[0_1px_2px_rgba(0,0,0,0.7)]"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="oklch(71% 0.19 13)"
            >
              <path d="M12 21s-7.5-4.7-10-9.3C.3 8.6 2.4 4.5 6.2 4.5c2.2 0 3.9 1.2 5.8 3.4 1.9-2.2 3.6-3.4 5.8-3.4 3.8 0 5.9 4.1 4.2 7.2C19.5 16.3 12 21 12 21Z" />
            </svg>
          )}
        </Link>
        {data.selectable && (
          <button
            type="button"
            aria-label={selected ? 'Deselect' : 'Select'}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              data.onToggleSelect?.(asset.id);
            }}
            className={
              'absolute left-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-full border-2 shadow transition-colors ' +
              (selected
                ? 'border-sky-500 bg-sky-500 text-white'
                : 'border-white/90 bg-black/40 text-transparent hover:text-white')
            }
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

export function TimelineGrid({
  assets,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  emptyLabel = 'No photos.',
  selectable = false,
  selectedIds,
  onToggleSelect,
  scrollKey,
}: TimelineGridProps) {
  const { ref, width, height } = useContainerSize();

  const columnCount = useMemo(() => {
    if (width <= 0) return MIN_COLUMNS;
    const raw = Math.floor(width / TARGET_COLUMN_WIDTH);
    return Math.max(MIN_COLUMNS, Math.min(MAX_COLUMNS, raw || MIN_COLUMNS));
  }, [width]);

  const cellSize = useMemo(() => {
    if (width <= 0 || columnCount <= 0) return TARGET_COLUMN_WIDTH;
    return Math.floor(width / columnCount);
  }, [width, columnCount]);

  // `now` is fixed for the component's lifetime so Today/Yesterday labels
  // don't reshuffle on every render. Good enough for a session.
  const now = useMemo(() => new Date(), []);

  const { rows } = useMemo(
    () => groupByDay(assets, columnCount, now),
    [assets, columnCount, now],
  );

  // Floating month/year pill — reflects the topmost visible section.
  const [stickyLabel, setStickyLabel] = useState<string | null>(null);

  const gridRef = useRef<VariableSizeGrid<CellData> | null>(null);
  // Whenever the column count OR the row tally changes, VariableSizeGrid
  // caches need a reset to recompute offsets.
  useEffect(() => {
    gridRef.current?.resetAfterIndices({
      columnIndex: 0,
      rowIndex: 0,
      shouldForceUpdate: true,
    });
  }, [columnCount, rows.length, cellSize]);

  const rowHeightFn = useCallback(
    (index: number) => {
      const r = rows[index];
      if (!r) return 0;
      if (r.kind === 'header') return HEADER_ROW_HEIGHT;
      return cellSize + PHOTO_ROW_GAP;
    },
    [rows, cellSize],
  );

  const handleItemsRendered = useCallback(
    (props: GridOnItemsRenderedProps) => {
      if (rows.length === 0) return;
      // Floating pill: walk back from the first visible row to the nearest
      // header and surface its month/year. At the very top of the list the
      // first day header is already on screen — hide the pill instead of
      // overlapping it.
      if (props.visibleRowStartIndex === 0) {
        setStickyLabel(null);
      } else {
        for (let r = props.visibleRowStartIndex; r >= 0; r--) {
          const row = rows[r];
          if (row?.kind === 'header') {
            setStickyLabel(row.monthYear);
            break;
          }
        }
      }
      // Fire the next-page fetch when we're within FETCH_AHEAD_ROWS of
      // the end of the materialized row list.
      if (!hasNextPage || isFetchingNextPage) return;
      if (props.visibleRowStopIndex >= rows.length - 1 - FETCH_AHEAD_ROWS) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, rows, fetchNextPage],
  );

  const handleScroll = useCallback(
    ({ scrollTop }: GridOnScrollProps) => {
      if (scrollKey) saveScrollOffset(scrollKey, scrollTop);
    },
    [scrollKey],
  );

  // Bring the lightbox's last-viewed asset into view, once, after the
  // first non-empty data lands. The saved scrollTop (applied via
  // `initialScrollTop` below) already covers the common "viewed one
  // photo, came straight back" case — we only move if the user arrowed
  // to a photo outside the restored viewport.
  const restoredViewedRef = useRef(false);
  useEffect(() => {
    if (restoredViewedRef.current) return;
    if (!scrollKey || assets.length === 0 || rows.length === 0) return;
    if (width <= 0 || height <= 0) return;
    restoredViewedRef.current = true;

    const viewedID = consumeViewedAsset();
    if (!viewedID) return;
    const assetIndex = assets.findIndex((a) => a.id === viewedID);
    if (assetIndex === -1) return;

    let offset = 0;
    let targetOffset = -1;
    let targetHeight = 0;
    for (const r of rows) {
      const h = r.kind === 'header' ? HEADER_ROW_HEIGHT : cellSize + PHOTO_ROW_GAP;
      if (
        r.kind === 'photos' &&
        assetIndex >= r.firstAssetIndex &&
        assetIndex < r.firstAssetIndex + r.count
      ) {
        targetOffset = offset;
        targetHeight = h;
        break;
      }
      offset += h;
    }
    if (targetOffset < 0) return;

    const viewTop = getScrollOffset(scrollKey);
    const fullyVisible =
      targetOffset >= viewTop && targetOffset + targetHeight <= viewTop + height;
    if (fullyVisible) return;

    const centered = Math.max(0, targetOffset - (height - targetHeight) / 2);
    gridRef.current?.scrollTo({ scrollTop: centered });
  }, [assets, rows, scrollKey, width, height, cellSize]);

  const data = useMemo<CellData>(
    () => ({ rows, assets, columnCount, cellSize, selectable, selectedIds, onToggleSelect }),
    [rows, assets, columnCount, cellSize, selectable, selectedIds, onToggleSelect],
  );

  const itemKey = useCallback(
    ({ columnIndex, rowIndex, data: d }: { columnIndex: number; rowIndex: number; data: CellData }) => {
      const r = d.rows[rowIndex];
      if (!r) return `${rowIndex}:${columnIndex}`;
      if (r.kind === 'header') return r.key;
      const a = d.assets[r.firstAssetIndex + columnIndex];
      return a ? `a:${a.id}` : `${r.key}:${columnIndex}`;
    },
    [],
  );

  return (
    <div ref={ref} className="relative h-full w-full overflow-hidden">
      {stickyLabel && assets.length > 0 && (
        <div className="pointer-events-none absolute left-3 top-2 z-20 rounded-full bg-neutral-900/85 px-3 py-1 font-display text-xs font-medium text-neutral-200 shadow ring-1 ring-white/10 backdrop-blur">
          {stickyLabel}
        </div>
      )}
      {assets.length === 0 && !hasNextPage ? (
        <div className="flex h-full flex-col items-center justify-center gap-3 text-neutral-500">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="text-neutral-700"
            aria-hidden
          >
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <circle cx="9" cy="10" r="1.8" />
            <path d="m5 18 5.2-5.2a1.5 1.5 0 0 1 2.1 0L19 19" />
          </svg>
          <span className="text-sm">{emptyLabel}</span>
        </div>
      ) : width > 0 && height > 0 ? (
        <VariableSizeGrid<CellData>
          ref={gridRef}
          width={width}
          height={height}
          columnCount={columnCount}
          rowCount={rows.length}
          columnWidth={() => cellSize}
          rowHeight={rowHeightFn}
          itemData={data}
          itemKey={itemKey}
          onItemsRendered={handleItemsRendered}
          onScroll={handleScroll}
          initialScrollTop={scrollKey ? getScrollOffset(scrollKey) : 0}
          overscanRowCount={2}
        >
          {Cell}
        </VariableSizeGrid>
      ) : null}
    </div>
  );
}
